/**
 * taskShared.jsx — ของใช้ร่วมของระบบงาน (/tasks, หน้างาน, ขั้นตอนรอบผลิต)
 */
import React, { useEffect, useMemo } from "react";
import {
  AlertTriangle, CalendarClock, Flame, Users, User, FileText, X, ExternalLink,
  Hourglass, CircleDot, Clock, PauseCircle, CheckCircle2, XCircle, ChevronRight, Undo2, ShieldCheck,
} from "lucide-react";
import socket from "../../socket";
import i18n from "../../i18n";
import { fileSize } from "../chat/chatShared";
import { useTx } from "../../utils/useTx";

// 2 ภาษา (22 ต.ค. 69) — label = ไทย (หน้าอื่นอ่าน STATUS[x].label ตรง ๆ อยู่) · en = อังกฤษ
const isEn = () => Boolean(i18n.language?.startsWith("en"));

// ── ภาษาสถานะเดียวกันทุกหน้า (28 ก.ย. 69) — สี + ไอคอน + คำ ไม่ใช้สีอย่างเดียว ──────────────
//   เขียว เสร็จ · ฟ้า รอทำ · ม่วง กำลังทำ · เทา รอก้อนก่อนหน้า · เหลือง ติดปัญหา · แดง เลยกำหนด (DueCountdown)
//   ม่วงเข้ม รอหัวหน้าตรวจ (REVIEW · migration 092 — คนทำส่งแล้ว หัวหน้ายังไม่กดผ่าน งานยังไม่ถูกส่งต่อ)
//   ไทม์ไลน์ CEO (ProjectTrackingScreen) และหน้าแจกงานใช้ชุดสีเดียวกันนี้
//   (2 ต.ค. 69 · skill pagetool-ui-staff) ป้ายเป็นสี่เหลี่ยมมุมมนนิดเดียว ตัวหนังสือ 13px ไม่ใช่ป้ายกลมตัวจิ๋ว · ปีเต็ม 2569
export const STATUS = {
  WAITING: { label: "รอขั้นก่อนหน้า", en: "Waiting for previous step", cls: "bg-slate-100 text-slate-600 border-slate-300", icon: Hourglass },
  TODO: { label: "รอทำ", en: "To do", cls: "bg-sky-50 text-sky-800 border-sky-300", icon: CircleDot },
  DOING: { label: "กำลังทำ", en: "In progress", cls: "bg-indigo-50 text-indigo-800 border-indigo-300", icon: Clock },
  BLOCKED: { label: "ติดปัญหา", en: "Blocked", cls: "bg-amber-50 text-amber-900 border-amber-400", icon: PauseCircle },
  REVIEW: { label: "รอหัวหน้าตรวจ", en: "Waiting for review", cls: "bg-violet-50 text-violet-800 border-violet-300", icon: ShieldCheck },
  DONE: { label: "เสร็จ", en: "Done", cls: "bg-emerald-50 text-emerald-800 border-emerald-300", icon: CheckCircle2 },
  CANCELLED: { label: "ยกเลิก", en: "Cancelled", cls: "bg-slate-100 text-slate-500 border-slate-300 line-through", icon: XCircle },
};
export const OPEN_STATUSES = ["WAITING", "TODO", "DOING", "BLOCKED", "REVIEW"];
// ชื่อสถานะตามภาษาที่เลือก — ใช้แทน STATUS[x].label ในหน้าที่แปลแล้ว
export const statusLabel = (status) => {
  const s = STATUS[status];
  return s ? (isEn() ? s.en : s.label) : status;
};

export const StatusPill = ({ status }) => {
  useTx(); // วาดใหม่เมื่อเปลี่ยนภาษา
  const s = STATUS[status] || STATUS.TODO;
  const Icon = s.icon;
  return (
    <span className={`inline-flex items-center gap-1 rounded-md border px-2 py-0.5 text-[13px] font-bold whitespace-nowrap ${s.cls}`}>
      {Icon && <Icon className="w-3.5 h-3.5" aria-hidden="true" />}{statusLabel(status in STATUS ? status : "TODO")}
    </span>
  );
};

export const UrgentBadge = () => {
  const tx = useTx();
  return (
    <span className="inline-flex items-center gap-1 rounded-md border border-orange-300 bg-orange-50 px-2 py-0.5 text-[13px] font-bold text-orange-800 whitespace-nowrap">
      <Flame className="w-3.5 h-3.5" /> {tx("ด่วน", "Urgent")}
    </span>
  );
};

const todayYmd = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};
// วันที่ตามภาษา (ชื่อเดิม thDay/thDateTime — หน้าอื่นใช้อยู่หลายที่) · ไทย = พ.ศ. · อังกฤษ = 22 Oct 2026
export const thDay = (ymd) => {
  if (!ymd) return "—";
  const [y, m, d] = String(ymd).slice(0, 10).split("-").map(Number);
  return new Date(y, m - 1, d).toLocaleDateString(isEn() ? "en-GB" : "th-TH", { day: "numeric", month: "short", year: "numeric" });
};
export const thDateTime = (v) =>
  v ? new Date(v).toLocaleString(isEn() ? "en-GB" : "th-TH", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" }) : "—";

// กำหนดส่ง — เลยกำหนดแล้วเป็นสีแดง วันนี้เป็นสีส้ม
export const DueLabel = ({ due, status }) => {
  const tx = useTx();
  if (!due) return <span className="text-[13px] text-slate-500">{tx("ไม่มีกำหนด", "No due date")}</span>;
  const open = OPEN_STATUSES.includes(status);
  const today = todayYmd();
  const tone = !open ? "text-slate-500" : due < today ? "text-rose-700 font-bold" : due === today ? "text-orange-700 font-bold" : "text-slate-700";
  return (
    <span className={`inline-flex items-center gap-1 text-[15px] whitespace-nowrap ${tone}`}>
      {open && due < today ? <AlertTriangle className="w-3.5 h-3.5" /> : <CalendarClock className="w-3.5 h-3.5" />}
      {due === today && open ? tx("วันนี้", "Today") : thDay(due)}
    </span>
  );
};

// จำนวนวันจากวันนี้ถึงวันกำหนด (ติดลบ = เลยมาแล้ว) — เทียบแบบวันท้องถิ่น
export const daysLeft = (due) => {
  if (!due) return null;
  const [y, m, d] = String(due).slice(0, 10).split("-").map(Number);
  const [ty, tm, td] = todayYmd().split("-").map(Number);
  return Math.round((new Date(y, m - 1, d) - new Date(ty, tm - 1, td)) / 86400000);
};

// กำหนดส่งเป็นคำพูด — "ช้า 2 วัน" / "ครบกำหนดวันนี้" / "พรุ่งนี้" / "เหลือ 4 วัน" (ไม่ต้องคิดเลขเอง)
export const DueCountdown = ({ due, status, className = "" }) => {
  const tx = useTx();
  if (!OPEN_STATUSES.includes(status)) return null;
  const n = daysLeft(due);
  if (n === null) return <span className={`text-[13px] text-slate-500 whitespace-nowrap ${className}`}>{tx("ไม่มีกำหนด", "No due date")}</span>;
  const [text, tone] = n < 0 ? [tx(`ช้า ${-n} วัน`, `${-n} day${-n > 1 ? "s" : ""} late`), "border-rose-600 bg-rose-600 text-white"]
    : n === 0 ? [tx("ครบกำหนดวันนี้", "Due today"), "border-orange-300 bg-orange-100 text-orange-900"]
      : n === 1 ? [tx("พรุ่งนี้", "Tomorrow"), "border-amber-300 bg-amber-50 text-amber-900"]
        : [tx(`เหลือ ${n} วัน`, `${n} days left`), "border-slate-300 bg-slate-100 text-slate-700"];
  return (
    <span className={`inline-flex items-center gap-1 rounded-md border px-2 py-0.5 text-[13px] font-bold whitespace-nowrap ${tone} ${className}`} title={thDay(due)}>
      {n < 0 && <AlertTriangle className="w-3.5 h-3.5" aria-hidden="true" />}{text}
    </span>
  );
};

// สายงานของก้อนงานโปรเจกต์ — ชิปเรียงตามลำดับก้อน · ก้อนของงานนี้ขอบเข้ม · ↩n = ถูกตีกลับกี่ครั้ง
// items จากหลังบ้าน (utils/taskPlan.js pipelineOf) · onOpen(id) กดเปิดก้อนนั้น (ไม่ส่ง = กดไม่ได้)
export function PipelineStrip({ items, onOpen, meLabel }) {
  const tx = useTx();
  if (!items?.length) return null;
  const me = meLabel || tx("งานนี้", "This task");
  return (
    <ol className="flex flex-wrap items-center gap-1" aria-label={tx("สายงาน", "Pipeline")}>
      {items.map((it, i) => {
        const s = STATUS[it.status] || STATUS.TODO;
        const Icon = s.icon;
        const Tag = onOpen && !it.current ? "button" : "span";
        return (
          <li key={it.id} className="flex items-center gap-1">
            <Tag
              {...(Tag === "button" ? { type: "button", onClick: (e) => { e.stopPropagation(); onOpen(it.id); } } : {})}
              title={`${it.name} · ${statusLabel(it.status in STATUS ? it.status : "TODO")}${it.dept_name ? ` · ${it.assignee_name || it.dept_name}` : ""}`}
              className={`inline-flex items-center gap-1 rounded-md border px-2 py-0.5 text-[13px] font-semibold whitespace-nowrap ${s.cls} ${it.current ? "outline outline-2 outline-slate-900" : ""} ${Tag === "button" ? "hover:brightness-95" : ""}`}
            >
              {Icon && <Icon className="w-3.5 h-3.5 shrink-0" aria-hidden="true" />}
              <span className="max-w-[11rem] truncate">{it.current ? `${me}: ` : ""}{it.name}</span>
              {it.reject_count > 0 && (
                <span className="inline-flex items-center text-rose-600" title={tx(`ถูกตีกลับ ${it.reject_count} ครั้ง`, `Sent back ${it.reject_count} time(s)`)}>
                  <Undo2 className="w-3 h-3" aria-hidden="true" />{it.reject_count}
                </span>
              )}
            </Tag>
            {i < items.length - 1 && <ChevronRight className="w-3 h-3 text-slate-300 shrink-0" aria-hidden="true" />}
          </li>
        );
      })}
    </ol>
  );
}

// ใครรับงาน — คน (และแผนก) หรือแผนกที่ยังไม่มีคนรับ
export const Assignee = ({ t }) => <AssigneeInner t={t} />;
const AssigneeInner = ({ t }) => {
  const tx = useTx();
  return t.assignee_name ? (
    <span className="inline-flex items-center gap-1 text-[15px] text-slate-800 min-w-0">
      <User className="w-4 h-4 text-slate-500 shrink-0" />
      <span className="truncate">{t.assignee_name}</span>
      {t.dept_name && <span className="text-[13px] text-slate-600 truncate">· {t.dept_name}</span>}
    </span>
  ) : (
    <span className="inline-flex items-center gap-1 text-[15px] text-amber-800 min-w-0">
      <Users className="w-4 h-4 shrink-0" />
      <span className="truncate">{t.dept_name || "—"}</span>
      <span className="text-[13px] font-bold text-amber-800 whitespace-nowrap">· {tx("ยังไม่มีคนรับ", "Not taken yet")}</span>
    </span>
  );
};

// มีอะไรเปลี่ยนในงาน (socket) → ดึงใหม่
export function useTaskRefresh(refetch) {
  useEffect(() => {
    const on = () => refetch();
    socket.on("task:event", on);
    return () => socket.off("task:event", on);
  }, [refetch]);
}

export const errMsg = (err, fallback) => err?.data?.message || fallback;

// ── ไฟล์แนบ ───────────────────────────────────────────────────────────────
// เปิดดูในแท็บใหม่ (รูป/PDF เปิดในเบราว์เซอร์ได้เลย) — ดาวน์โหลดบนเครื่องผู้ใช้ได้ชื่อไฟล์เพี้ยน จึงเน้นเปิดดู
export function FileGallery({ files, compact = false }) {
  if (!files?.length) return null;
  const images = files.filter((f) => f.is_image);
  const others = files.filter((f) => !f.is_image);
  return (
    <div className="space-y-2">
      {images.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {images.map((f) => (
            <a key={f.id} href={f.url} target="_blank" rel="noopener noreferrer" title={f.orig_name} className="block">
              <img
                src={f.url}
                alt={f.orig_name}
                loading="lazy"
                className={`${compact ? "h-24" : "h-36"} max-w-[16rem] object-cover rounded-xl ring-1 ring-slate-200 bg-white hover:ring-slate-400`}
              />
            </a>
          ))}
        </div>
      )}
      {others.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {others.map((f) => (
            <a
              key={f.id}
              href={f.url}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 rounded-md bg-slate-50 border border-slate-300 px-3 py-2 text-[15px] text-slate-800 hover:bg-white hover:ring-slate-400 max-w-full"
            >
              <FileText className="w-4 h-4 shrink-0 text-slate-400" />
              <span className="truncate max-w-[18rem]">{f.orig_name}</span>
              <span className="text-[13px] text-slate-500 shrink-0">{fileSize(f.size_bytes)}</span>
              <ExternalLink className="w-3.5 h-3.5 shrink-0 text-slate-400" />
            </a>
          ))}
        </div>
      )}
    </div>
  );
}

// ไฟล์ที่เลือกไว้ รอส่ง — รูปโชว์ภาพย่อ กด X เอาออก
export function PendingFiles({ files, onRemove }) {
  const previews = useMemo(
    () => files.map((f) => (f.type?.startsWith("image/") ? URL.createObjectURL(f) : null)),
    [files],
  );
  useEffect(() => () => previews.forEach((u) => u && URL.revokeObjectURL(u)), [previews]);
  if (!files.length) return null;
  return (
    <div className="flex flex-wrap gap-2">
      {files.map((f, i) => (
        <div key={`${f.name}-${i}`} className="relative group">
          {previews[i] ? (
            <img src={previews[i]} alt={f.name} className="h-20 w-20 object-cover rounded-xl ring-1 ring-slate-200" />
          ) : (
            <div className="h-20 w-40 rounded-xl ring-1 ring-slate-200 bg-slate-50 px-2.5 py-2 flex flex-col justify-between">
              <FileText className="w-4 h-4 text-slate-400" />
              <span className="text-[13px] text-slate-700 truncate">{f.name}</span>
              <span className="text-[13px] text-slate-500">{fileSize(f.size)}</span>
            </div>
          )}
          <button
            type="button"
            onClick={() => onRemove(i)}
            className="absolute -top-1.5 -right-1.5 w-5 h-5 rounded-full bg-slate-900 text-white flex items-center justify-center"
            aria-label={`${isEn() ? "Remove" : "เอาออก"} ${f.name}`}
          >
            <X className="w-3 h-3" />
          </button>
        </div>
      ))}
    </div>
  );
}
